import {
  PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { NewlyCreated } from '@/modules/common/AddNewAnythingModal/AddNewAnythingModal';
import { diagnostics } from '@/utils/diagnostics';
import { englishJoinStringList } from '@/utils/formatting/strings';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { DPFormRecipientTrackerContext } from './DPFormRecipientTracker.context';

type DPFormRecipientTrackerProviderProps = PropsWithChildren;

function useContextValue() {
  // Whether the user has added a new Testamentary Entity recipient
  // during the current edit session
  const [hasAddedNewTERecipients, setHasAddedNewTERecipients] = useState(false);

  // Newly added TE recipients during the current edit session, mapped by ID
  const [newlyAddedTERecipients, setNewlyAddedTERecipients] = useState<
    Record<string, NewlyCreated>
  >({});

  // Whether the confirmation modal for navigating to manage DP is open
  const [isConfirmNavManageDPModalOpen, setIsConfirmNavManageDPModalOpen] =
    useState(false);

  // Display names of newly added Testamentary Entity recipients
  const newlyAddedTERecipientDisplayNames = useMemo(() => {
    return Object.values(newlyAddedTERecipients).map(
      (recipient) => recipient.displayName
    );
  }, [newlyAddedTERecipients]);

  // Handles when a new recipient has been created from the recipients list.
  // Marks hasAddedNewTERecipients as true if the recipient is a TE,
  // that has been added to the DP Form as the first recipient.
  const handleNewlyAddedRecipient = useCallback(
    (recipient: NewlyCreated) => {
      diagnostics.debug(
        '[DPFormRecipientTrackerProvider][handleNewlyAddedRecipient]',
        {
          hasAddedNewTERecipients,
          recipient,
        }
      );

      if (
        !recipient.id ||
        newlyAddedTERecipients[recipient.id] ||
        getPulidKind(recipient.id) !== PulidKind.TestamentaryEntity
      ) {
        // Already added this recipient, or not a TE
        return;
      }

      // Add the recipient to the list of newly added TE recipients
      setNewlyAddedTERecipients((prev) => ({
        ...prev,
        [recipient.id]: recipient,
      }));

      // Set hasAddedNewTERecipients to true (if not already)
      if (!hasAddedNewTERecipients) {
        setHasAddedNewTERecipients(true);
      }
    },
    [hasAddedNewTERecipients, newlyAddedTERecipients]
  );

  // Reference to the close function of the outer modal, and the confirmation
  // action of the confirmation modal. This is set when
  // handleRequestCloseOuterModal is called, and used in the callback of the
  // confirmation modal.
  const closeOuterModalRef = useRef<(() => void) | undefined | null>(null);
  const confirmNavigateManageDPRef = useRef<(() => void) | undefined | null>(
    null
  );

  const handleRequestCloseOuterModal = useCallback(
    (closeOuterModal?: () => void) => {
      // With the DP Forms V2, we don't need to ask them to go to the Manage DP page
      closeOuterModal?.();
    },
    []
  );

  // Handles closing the confirmation modal for navigating to manage DP
  const handleCloseConfirmNavManageDPModal = useCallback(
    (reason: 'confirm' | 'cancel') => {
      setIsConfirmNavManageDPModalOpen(false);

      if (reason === 'cancel') {
        closeOuterModalRef?.current?.();
        return;
      }

      // User confirmed they want to navigate to manage DP
      confirmNavigateManageDPRef?.current?.();
    },
    []
  );

  return useMemo(
    () => ({
      hasAddedNewTERecipients,
      handleNewlyAddedRecipient,
      newlyAddedTERecipientDisplayNames,
      handleRequestCloseOuterModal,
      isConfirmNavManageDPModalOpen,
      handleCloseConfirmNavManageDPModal,
    }),
    [
      hasAddedNewTERecipients,
      handleNewlyAddedRecipient,
      newlyAddedTERecipientDisplayNames,
      handleRequestCloseOuterModal,
      isConfirmNavManageDPModalOpen,
      handleCloseConfirmNavManageDPModal,
    ]
  );
}

export function DPFormRecipientTrackerProvider({
  children,
}: DPFormRecipientTrackerProviderProps) {
  const value = useContextValue();

  const {
    isConfirmNavManageDPModalOpen,
    handleCloseConfirmNavManageDPModal,
    newlyAddedTERecipientDisplayNames,
  } = value;

  const modalText = useMemo(() => {
    if (newlyAddedTERecipientDisplayNames.length > 1) {
      const isPlural = newlyAddedTERecipientDisplayNames.length > 1;
      const jointedName = englishJoinStringList(
        newlyAddedTERecipientDisplayNames,
        'and'
      );
      return `Proceed to specify how ${jointedName} ${isPlural ? 'distribute' : 'distributes'} assets upon termination`;
    }
    return 'Proceed to specify how sub-trusts distribute assets upon termination';
  }, [newlyAddedTERecipientDisplayNames]);

  return (
    <DPFormRecipientTrackerContext.Provider value={value}>
      {children}
      {isConfirmNavManageDPModalOpen && (
        <ConfirmationModal
          isOpen={isConfirmNavManageDPModalOpen}
          heading="Enter dispositive provisions for sub-trusts?"
          cancelText="Skip"
          confirmText="Proceed"
          onClose={handleCloseConfirmNavManageDPModal}
        >
          {modalText}
        </ConfirmationModal>
      )}
    </DPFormRecipientTrackerContext.Provider>
  );
}
